/**
 * LinkAccountPage — Google OAuth account-linking confirmation screen.
 *
 * Design: project/designs/39b-google-signin-ui.md
 *
 * Route: /auth/link-account?provider=google&email=<url-encoded-email>
 *
 * Rendered when the Google OAuth callback detects that the Google-provided
 * email matches an existing password account (backend AC-4 / WI-39a). The
 * user must explicitly confirm account linking before the backend creates the
 * user_identities row and issues a session.
 *
 * AC-5: "Link accounts" calls POST /api/v1/auth/google/link-confirm.
 *       On success (HTTP 200 + { return_to }): navigates to return_to.
 *       On error (HTTP 400/401): shows stale-session error state.
 * AC-6: Navigating here with no pending link → link-confirm returns 400;
 *       page shows error state with link to /login (no crash, no blank screen).
 *
 * OQ-39b-1 resolution (Option A): email is read from the URL query param
 * ?email=. The backend includes it in the redirect from the OAuth callback.
 * The email is not a secret (the user just typed it at Google).
 *
 * Return URL: the backend stores the original return URL in the session during
 * google_initiate. The link-confirm response returns { return_to } which is
 * used for the post-linking navigation. The frontend does not need to forward
 * a return URL in the link-confirm body.
 */

import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthCard } from './AuthCard';
import { linkGoogleAccount } from '../../api/auth';

type PageState = 'idle' | 'linking' | 'error';

export function LinkAccountPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // The backend includes the Google email in the redirect URL for display purposes.
  const googleEmail = searchParams.get('email') ?? '';

  const [pageState, setPageState] = useState<PageState>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  async function handleLinkAccounts() {
    setPageState('linking');
    setErrorMessage('');
    try {
      const result = await linkGoogleAccount();
      // On success, navigate to the return URL from the server (session-stored).
      navigate(result.return_to, { replace: true });
    } catch {
      // HTTP 400 or 401 → stale or missing pending_oauth_link in session.
      setErrorMessage('Your session has expired. Please sign in again.');
      setPageState('error');
    }
  }

  function handleUseDifferentAccount() {
    navigate('/login');
  }

  // Error state: stale session or no pending link in session.
  if (pageState === 'error') {
    return (
      <AuthCard>
        <h1 className="text-lg font-bold text-gray-900 mb-4">Link your accounts</h1>
        <p
          role="alert"
          className="text-sm text-red-600 flex items-start gap-1 mb-5"
        >
          <span aria-hidden="true">&#9888;</span>
          {errorMessage}
        </p>
        <Link
          to="/login"
          className="text-sm text-brand-primary hover:underline font-medium"
        >
          Go to log in &rarr;
        </Link>
      </AuthCard>
    );
  }

  const linking = pageState === 'linking';

  return (
    <AuthCard>
      <h1 className="text-lg font-bold text-gray-900 mb-4">Link your accounts</h1>

      <p className="text-sm text-gray-700 mb-2">
        A password account already exists for:
      </p>

      {googleEmail && (
        <p className="text-sm font-medium text-gray-900 mb-4 break-all">
          {googleEmail}
        </p>
      )}

      <p className="text-sm text-gray-700 mb-6">
        Link your Google account to it so you can sign in with either method.
      </p>

      <button
        type="button"
        onClick={handleLinkAccounts}
        disabled={linking}
        className="w-full bg-brand-primary text-white font-semibold py-2.5 rounded-lg hover:bg-brand-primary-dk focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 motion-safe:transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-4"
      >
        {linking ? 'Linking…' : 'Link accounts'}
      </button>

      <div className="text-center">
        <button
          type="button"
          onClick={handleUseDifferentAccount}
          disabled={linking}
          className="text-sm text-brand-primary hover:underline disabled:opacity-50"
        >
          Use a different Google account
        </button>
      </div>
    </AuthCard>
  );
}
